// The detail level, app-side (DETAIL_LEVELS.md). One per person: how much
// rides on the face of a row or card.
//
// It lives in its own localStorage key rather than on the session, because a
// visitor in the tutorial has no session and still gets to choose. Signed in,
// the account's level is put here and a change here is saved to the account
// (`accountPrefs.tsx`, B-245), unless this device keeps its own (B-246); this
// key is then the cache that paints the first frame.
//
// The level ONLY controls the face. A popover, the detail sheet, an
// expandable group and every action are reachable at every level.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_DETAIL, atLeast, parseDetailLevel, type DetailLevel } from '@fsp/theme';
import { storageKey } from './storage';

export const DETAIL_KEY = storageKey('detail.v1');

/** The session blob, read once to migrate anyone who had Pro mode on. */
const SESSION_KEY = 'fsp.session.v1';

/**
 * Pro mode was a boolean; it is the top of a three-stop range now. Someone who
 * had it on wants Pro, and everyone else starts at the default. Runs once:
 * the result is written to DETAIL_KEY, which wins from then on.
 */
function migrateFromProMode(): DetailLevel | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && 'proMode' in parsed) {
      return (parsed as { proMode?: unknown }).proMode === true ? 'pro' : DEFAULT_DETAIL;
    }
    return null;
  } catch {
    return null;
  }
}

export function readStoredDetail(): DetailLevel {
  try {
    const raw = localStorage.getItem(DETAIL_KEY);
    if (raw !== null) return parseDetailLevel(raw);
    const migrated = migrateFromProMode();
    if (migrated) {
      localStorage.setItem(DETAIL_KEY, migrated);
      return migrated;
    }
  } catch {
    // Storage blocked: the default is a fine place to spend a page load.
  }
  return DEFAULT_DETAIL;
}

/**
 * Stamp the root so CSS can do the work — row height, panel padding, and every
 * `[data-detail]` rule — with no prop drilling and no re-render.
 * `apps/web/index.html` does the same before React mounts; keep them in sync.
 */
export function stampDetail(level: DetailLevel): void {
  document.documentElement.setAttribute('data-detail', level);
}

interface DetailContextValue {
  detail: DetailLevel;
  setDetail: (level: DetailLevel) => void;
}

const DetailContext = createContext<DetailContextValue | null>(null);

export function DetailProvider({ children }: { children: ReactNode }) {
  const [detail, setLevel] = useState<DetailLevel>(readStoredDetail);

  useEffect(() => {
    stampDetail(detail);
  }, [detail]);

  const setDetail = useCallback((level: DetailLevel) => {
    setLevel(level);
    try {
      localStorage.setItem(DETAIL_KEY, level);
    } catch {
      // Storage blocked: the choice holds for this page load.
    }
  }, []);

  const value = useMemo(() => ({ detail, setDetail }), [detail, setDetail]);
  return <DetailContext.Provider value={value}>{children}</DetailContext.Provider>;
}

export function useDetailContext(): DetailContextValue {
  const ctx = useContext(DetailContext);
  if (!ctx) throw new Error('useDetail must be used inside DetailProvider');
  return ctx;
}

/** The current level. Most components want `<Detail min>` instead. */
export function useDetail(): DetailLevel {
  return useDetailContext().detail;
}

/** True at or past `min`. For deciding whether to compute something expensive. */
export function useAtLeast(min: DetailLevel): boolean {
  return atLeast(useDetail(), min);
}

/**
 * Show children only at or past `min`.
 *
 * Renders nothing below the threshold rather than hiding it with CSS, so the
 * accessibility tree matches what is on screen — which the headless QA walks
 * read, and screen readers announce.
 */
export function Detail({ min, children }: { min: DetailLevel; children: ReactNode }) {
  return useAtLeast(min) ? <>{children}</> : null;
}
